import type { ReactElement, SVGProps } from 'react';

/**
 * Iconos INTERNOS del composer (copiados del set de fit al extraer el package;
 * son genéricos: mic, pausa, play, tacho, flecha, ondas, candado, cruz). Todos
 * usan `currentColor` — el color lo pone el CSS del contenedor. Las apps no
 * los importan: si una app necesita el mismo dibujo en otra pantalla, lo tiene
 * en su propio set.
 */

type IconProps = SVGProps<SVGSVGElement>;

function base(props: IconProps): IconProps {
  return {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    ...props,
  };
}

/** Micrófono — botón de hold y estados de grabación. */
export function MicIcon(props: IconProps): ReactElement {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path
        d="M5.5 11.5a6.5 6.5 0 0 0 13 0"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
      />
      <path d="M12 18v3" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

/** Mic tachado — mute del modo charla (el mic queda en silencio, sin salir). */
export function MicOffIcon(props: IconProps): ReactElement {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path
        d="M5.5 11.5a6.5 6.5 0 0 0 13 0"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
      />
      <path d="M12 18v3" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
      <path d="M4 4l16 16" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
    </svg>
  );
}

/** Pausa — congelar la grabación fijada (dos barras, como WhatsApp). */
export function PauseIcon(props: IconProps): ReactElement {
  return (
    <svg {...base(props)}>
      <path d="M9 5v14M15 5v14" />
    </svg>
  );
}

/** Play — escuchar el take pausado. */
export function PlayIcon(props: IconProps): ReactElement {
  return (
    <svg {...base(props)}>
      <path d="M8 5.5v13l11-6.5-11-6.5Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Tacho — descartar la grabación push-to-talk fijada. */
export function TrashIcon(props: IconProps): ReactElement {
  return (
    <svg {...base(props)}>
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
    </svg>
  );
}

/** Flecha → (enviar). */
export function ArrowRightIcon(props: IconProps): ReactElement {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <path
        d="M2 8h11M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Ondas de voz — entrada al modo charla a manos libres del composer. */
export function WaveformIcon(props: IconProps): ReactElement {
  return (
    <svg {...base(props)}>
      <path d="M3 10.5v3M7.5 7v10M12 4.5v15M16.5 7v10M21 10.5v3" />
    </svg>
  );
}

/** Candado — hint de fijar la grabación (deslizar hacia arriba). */
export function LockIcon(props: IconProps): ReactElement {
  return (
    <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden {...props}>
      <rect x="2" y="5" width="8" height="5.5" rx="1.5" stroke="currentColor" strokeWidth={1.3} />
      <path d="M4 5V3.5a2 2 0 0 1 4 0V5" stroke="currentColor" strokeWidth={1.3} />
    </svg>
  );
}

/** Cruz — salir del modo charla. */
export function XIcon(props: IconProps): ReactElement {
  return (
    <svg width={11} height={11} viewBox="0 0 12 12" fill="none" aria-hidden {...props}>
      <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" />
    </svg>
  );
}
